import { isEmpty } from "lodash";
import qs from "qs";
import z from "zod";

import { SearchBidsRequest } from "lib/generated/app-service-gql/graphql";
import { BidEntityType, BidSortBy, BidSortOrder } from "lib/types/graphQLEnums";
import {
  AssigneeFilterSchema,
  ColumnSetting,
  CountryRegionSchema,
  DateFilterSchema,
  nonEmptyUserFilter,
  ValueFilterSchema,
} from "lib/types/models";
import type { BidDetails } from "../../lib/hooks/api/bid/bids/useBid";
import { encodeCompressed } from "../../lib/hooks/useURLState";
import { routes } from "../../lib/routes";
import { AssigneeFilterValue } from "../form_components/dropdown_list_selects/UserDropdownFilter";
import { filterObject } from "../leads/leads_table/utils";

const keysToKeep: (keyof BidDetails)[] = [
  "id",
  "name",
  "description",
  "value",
  "clarificationDeadline",
  "submissionDate",
  "startDate",
  "awardDate",
];
export type BidColumns =
  | "name"
  | "buyer"
  | "stage"
  | "assignedTo"
  | "createdBy"
  | "value"
  | "updatedAt"
  | "submissionDate"
  | "awardDate"
  | "supplier"
  | "framework";

export const ALL_BID_COLUMNS: ColumnSetting<BidColumns>[] = [
  { title: "Bid name", field: "name", disabled: true },
  { title: "Buyer", field: "buyer" },
  { title: "Stage", field: "stage" },
  { title: "Assigned to", field: "assignedTo" },
  { title: "Created by", field: "createdBy" },
  { title: "Value", field: "value" },
  { title: "Last updated", field: "updatedAt" },
  { title: "Submission date", field: "submissionDate" },
  { title: "Award date", field: "awardDate" },
  { title: "Supplier", field: "supplier" },
  { title: "Framework", field: "framework" },
];

// Columns shown by default. The "Created by" column is opt-in and excluded.
export const DEFAULT_VISIBLE_BID_COLUMNS: ColumnSetting<BidColumns>[] = ALL_BID_COLUMNS.filter(
  (c) => c.field !== "createdBy",
);

const columnCopies: Record<BidColumns, string> = ALL_BID_COLUMNS.reduce(
  (acc, { title, field }) => {
    if (field === "buyer") {
      return { ...acc, buyerName: title };
    }
    if (field === "stage") {
      return { ...acc, stageName: title };
    }
    return { ...acc, [field]: title };
  },
  {} as Record<BidColumns, string>,
);

export const getBidColumnName = (column?: string): string => {
  if (!column || !(column in columnCopies)) return "";
  return columnCopies[column as BidColumns];
};

/**
 * Filters bid data to only include fields that should be in the form
 */
export const filterBidDefaultValues = (bid: BidDetails) => {
  const filteredPayload = filterObject(bid, keysToKeep);
  return {
    ...filteredPayload,
    stageId: bid.stage?.id,
    assignedToId: bid.assignedTo?.guid || null,
  };
};

export const BidFiltersSchema = z.object({
  searchText: z.string().optional(),
  value: ValueFilterSchema.optional(),
  assignee: AssigneeFilterSchema.optional(),
  createdBy: AssigneeFilterSchema.optional(),
  stageIds: z.array(z.uuid()).optional(),
  createdDate: DateFilterSchema.optional(),
  lastUpdatedDate: DateFilterSchema.optional(),
  submissionDate: DateFilterSchema.optional(),
  awardDate: DateFilterSchema.optional(),
  buyers: z.array(z.uuid()).optional(),
  customBuyers: z.array(z.uuid()).optional(),
  buyerLists: z.array(z.uuid()).optional(),
  buyerCategories: z.array(z.uuid()).optional(),
  buyerCountryRegions: CountryRegionSchema.optional(),
  supplierGuids: z.array(z.uuid()).optional(),
  supplierSme: z.string().optional(),
  entityId: z.string().optional(),
  teamIds: z.array(z.uuid()).optional(),
  order: z.enum(BidSortOrder),
  orderBy: z.enum(BidSortBy),
});
export type BidFilters = z.infer<typeof BidFiltersSchema>;

export const EMPTY_BID_FILTERS: BidFilters = {
  searchText: "",
  assignee: { ids: [], includeNulls: undefined },
  createdBy: { ids: [], includeNulls: undefined },
  stageIds: [],
  createdDate: { filter: undefined, hideNulls: undefined },
  lastUpdatedDate: { filter: undefined, hideNulls: undefined },
  submissionDate: { filter: undefined, hideNulls: undefined },
  awardDate: { filter: undefined, hideNulls: undefined },
  value: { from: undefined, to: undefined, hideNulls: undefined },
  buyers: [],
  customBuyers: [],
  buyerLists: [],
  buyerCategories: [],
  buyerCountryRegions: { countries: [], regions: [], selected: [] },
  orderBy: BidSortBy.UpdatedAt,
  order: BidSortOrder.Desc,
  supplierGuids: [],
  supplierSme: undefined,
  teamIds: [],
};

export const DEFAULT_FILTERS: BidFilters = {
  orderBy: BidSortBy.UpdatedAt,
  order: BidSortOrder.Desc,
};

export const DEFAULT_PAGINATION: { pageSize: number; page: number } = {
  page: 1,
  pageSize: 10,
};

export type BidSortState = {
  field: BidSortBy;
  order: BidSortOrder;
};

export type SelectedBid = { id: string; stageId: string; name: string };

export function convertBidFiltersToSearchRequest(filters: BidFilters): SearchBidsRequest {
  let buyerFilters: SearchBidsRequest["buyerFilters"];
  if (
    filters.buyers?.length ||
    filters.customBuyers?.length ||
    filters.buyerLists?.length ||
    filters.buyerCategories?.length ||
    filters.buyerCountryRegions?.countries?.length ||
    filters.buyerCountryRegions?.regions?.length
  ) {
    buyerFilters = {
      buyers: filters.buyers,
      customBuyers: filters.customBuyers,
      buyerLists: filters.buyerLists,
      buyerCategories: filters.buyerCategories,
      buyerCountryCodes: filters.buyerCountryRegions?.countries,
      buyerRegions: filters.buyerCountryRegions?.regions,
    };
  }

  let supplierFilters: SearchBidsRequest["supplierFilters"];
  if (filters.supplierGuids?.length || filters.supplierSme) {
    supplierFilters = {
      ids: filters.supplierGuids?.filter(
        (id) => !["SIGNALS:ALL_COMPETITORS", "SIGNALS:ALL_PARTNERS"].includes(id),
      ),
      isSME: filters.supplierSme ? filters.supplierSme.toLowerCase() === "true" : undefined,
    };
  }

  return {
    searchText: filters.searchText,
    assignee: nonEmptyUserFilter(filters.assignee),
    createdBy: nonEmptyUserFilter(filters.createdBy),
    stageIds: filters.stageIds,
    createdDate: isEmpty(filters.createdDate) ? undefined : filters.createdDate,
    submissionDate: isEmpty(filters.submissionDate) ? undefined : filters.submissionDate,
    awardDate: isEmpty(filters.awardDate) ? undefined : filters.awardDate,
    lastUpdatedDate: isEmpty(filters.lastUpdatedDate) ? undefined : filters.lastUpdatedDate,
    value: isEmpty(filters.value) ? undefined : filters.value,
    buyerFilters,
    supplierFilters,
    teamIds: filters.teamIds,
    order: filters.order,
    orderBy: filters.orderBy,
  };
}

export const createBidSchema = z.object({
  name: z.string().min(1, "Name is required"), // Ensures name is required
  value: z.coerce.number().nonnegative().optional(),
  stageId: z.uuid({ message: "Stage is required" }), // Ensures stage is required
  assignedToId: z.uuid().optional(),
  leadId: z.uuid().optional(),
  submissionDate: z.coerce
    .date()
    .transform((date) => date.toISOString())
    .nullish(),
  awardDate: z.coerce
    .date()
    .transform((date) => date.toISOString())
    .nullish(),
  buyerIds: z.uuid().array().optional(),
  entities: z
    .object({
      entityId: z.uuid(),
      entityType: z.enum(BidEntityType),
    })
    .array(),
});

export const mapSortFieldToEnum = (field: string): BidSortBy => {
  switch (field) {
    case "name":
      return BidSortBy.Name;
    case "value":
      return BidSortBy.Value;
    case "createdAt":
      return BidSortBy.CreatedAt;
    case "updatedAt":
      return BidSortBy.UpdatedAt;
    case "buyerName":
      return BidSortBy.BuyerName;
    case "assignedTo":
      return BidSortBy.AssignedTo;
    case "createdBy":
      return BidSortBy.CreatedBy;
    case "stageName":
      return BidSortBy.StageName;
    case "submissionDate":
      return BidSortBy.SubmissionDate;
    case "awardDate":
      return BidSortBy.AwardDate;
    default:
      return BidSortBy.CreatedAt;
  }
};

export const mapSortOrderToEnum = (order: "ASC" | "DESC"): BidSortOrder => {
  return order === "ASC" ? BidSortOrder.Asc : BidSortOrder.Desc;
};

export function generateBidsSearchUrl(params: {
  stageIds?: string[];
  assignee?: AssigneeFilterValue;
  teamIds?: string[];
}): string {
  const filterValue = {
    stageIds: params.stageIds,
    assignee: params.assignee,
    teamIds: params.teamIds,
    orderBy: BidSortBy.UpdatedAt,
    order: BidSortOrder.Desc,
  };
  const queryParams = qs.stringify(
    {
      bidFilters: encodeCompressed(filterValue),
    },
    { arrayFormat: "brackets", encodeValuesOnly: true, addQueryPrefix: true },
  );
  return `${routes.bids}${queryParams}`;
}
